import { useEffect } from "react";

const CLARITY_ID = "z8tujsyebd";
const LEGACY_CONSENT_STORAGE_KEY = "cookie-consent";
const CLARITY_SCRIPT_SELECTOR = `script[src*="clarity.ms/tag/${CLARITY_ID}"]`;

type StoredConsentState = {
  hasConsented?: boolean;
  preferences?: {
    analytics?: boolean;
  };
};

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };

/** Clarity is analytics (heatmaps / session replay) — require analytics consent only. */
const hasAnalyticsConsent = () => {
  const raw = window.localStorage.getItem(LEGACY_CONSENT_STORAGE_KEY);
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as StoredConsentState;
    return Boolean(parsed.hasConsented && parsed.preferences?.analytics);
  } catch {
    return false;
  }
};

const loadClarity = () => {
  if (document.querySelector(CLARITY_SCRIPT_SELECTOR)) return;

  const w = window as Window & { clarity?: ClarityFn };
  w.clarity =
    w.clarity ||
    function (...args: unknown[]) {
      (w.clarity!.q = w.clarity!.q || []).push(args);
    };

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${CLARITY_ID}`;
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
};

const removeClarity = () => {
  document.querySelector(CLARITY_SCRIPT_SELECTOR)?.remove();

  const w = window as Window & { clarity?: ClarityFn };
  if (w.clarity) {
    w.clarity.q = [];
  }

  // Best-effort cleanup of Clarity cookies on consent withdraw.
  for (const entry of document.cookie.split(";")) {
    const name = entry.split("=")[0]?.trim();
    if (!name) continue;
    if (/^_cl/i.test(name) || /^CLID$/i.test(name) || /^ANONCHK$/i.test(name)) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }
  }
};

export const MicrosoftClarity = () => {
  useEffect(() => {
    const applyConsent = () => {
      if (hasAnalyticsConsent()) {
        loadClarity();
      } else {
        removeClarity();
      }
    };

    applyConsent();
    window.addEventListener("consent-updated", applyConsent);
    window.addEventListener("storage", applyConsent);

    return () => {
      window.removeEventListener("consent-updated", applyConsent);
      window.removeEventListener("storage", applyConsent);
    };
  }, []);

  return null;
};

declare global {
  interface Window {
    clarity?: ClarityFn;
  }
}
